import { createHash } from "node:crypto";
import { z } from "@brains/utils/zod";

type ActorRefSchema = z.ZodDiscriminatedUnion<
  [
    z.ZodObject<{
      kind: z.ZodLiteral<"user">;
      userId: z.ZodString;
      canonicalId: z.ZodOptional<z.ZodString>;
    }>,
    z.ZodObject<{
      kind: z.ZodLiteral<"external">;
      externalActorId: z.ZodString;
    }>,
    z.ZodObject<{ kind: z.ZodLiteral<"agent">; agentId: z.ZodString }>,
    z.ZodObject<{ kind: z.ZodLiteral<"service">; serviceId: z.ZodString }>,
  ]
>;

export const actorRefSchema: ActorRefSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("user"),
    userId: z.string().min(1),
    canonicalId: z.string().min(1).optional(),
  }),
  z.object({
    kind: z.literal("external"),
    externalActorId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("agent"),
    agentId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("service"),
    serviceId: z.string().min(1),
  }),
]);

export type ActorRef = z.output<typeof actorRefSchema>;

export interface LegacyActorIdentity {
  actorId: string;
  userId?: string | undefined;
  canonicalId?: string | undefined;
  interfaceType: string;
  role: "user" | "assistant";
}

export function actorRefFromLegacy(input: LegacyActorIdentity): ActorRef {
  const migratedUserId =
    input.userId ??
    (input.canonicalId?.startsWith("user:")
      ? `usr_${input.canonicalId.slice("user:".length)}`
      : undefined);
  if (migratedUserId) {
    return {
      kind: "user",
      userId: migratedUserId,
      ...(input.canonicalId ? { canonicalId: input.canonicalId } : {}),
    };
  }
  if (input.role === "assistant") {
    return { kind: "agent", agentId: input.actorId };
  }
  return {
    kind: "external",
    externalActorId: input.canonicalId
      ? createExternalActorId("canonical", input.canonicalId)
      : createExternalActorId(input.interfaceType, input.actorId),
  };
}

export function authenticatedUserId(context: {
  actor?: ActorRef | undefined;
}): string | undefined {
  return context.actor?.kind === "user" ? context.actor.userId : undefined;
}

export function actorRefKey(actor: ActorRef): string {
  switch (actor.kind) {
    case "user":
      return `user:${actor.userId}`;
    case "external":
      return `external:${actor.externalActorId}`;
    case "agent":
      return `agent:${actor.agentId}`;
    case "service":
      return `service:${actor.serviceId}`;
  }
}

export function createExternalActorId(source: string, subject: string): string {
  const qualifiedSubject = subject.startsWith(`${source}:`)
    ? subject
    : `${source}:${subject}`;
  const digest = createHash("sha256").update(qualifiedSubject).digest("hex");
  return `ext_${digest}`;
}
